import colors from '@/styles/colors'
import { ConnectionModel, MyConnection, SearchConnection } from '@/types'
import { SIDE_PANEL_WIDTH } from '@/utils/constants'
import closeIcon from '@/assets/svgs/close-round-colored.svg'
import SVG from 'react-inlinesvg'
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Radio,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { UserAvatar } from '@/components'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { useEffect, useMemo, useState } from 'react'
import { group } from 'console'
import { RoundedActionButton } from '@/components/StyledComponents'
import { pluralize } from '@/utils/helper'
import { useGetMyCircles } from '@/hooks/api/dashboard'

interface CirclesSelectDrawerProps {
  open: boolean
  onClose: () => void
  user: ConnectionModel | null
}

const CirclesSelectDrawer = ({ open, onClose, user }: CirclesSelectDrawerProps) => {
  const theme = useTheme()
  const getAllGroups = useGetMyCircles()
  const [myGroups] = useAppStore((state) => [state.user.myGroups], shallow)
  const lessThanSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedCircleID, setSelectedCircleID] = useState('')

  useEffect(() => {
    if (!myGroups) getAllGroups.mutate()
  }, [])

  const systemGroups = useMemo(() => {
    return myGroups.filter((i) => i.isSystemProvided === '1') || []
  }, [myGroups])

  const handleCircleSelection =
    (circleID: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedCircleID(circleID)
      }
    }

  return (
    <Drawer
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: lessThanSmall ? '100%' : SIDE_PANEL_WIDTH,
          height: '100%',
          borderWidth: 0,
        },
      }}
      variant='temporary'
      anchor='right'
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{
          p: '30px',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          backgroundColor: colors.pageBackground,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: '#475496', position: 'absolute', right: '16px', top: '16px', zIndex: 10 }}
        >
          <SVG src={closeIcon} width={30} height={30} />
        </IconButton>
        <Typography variant='labelLarge'>{`Who is ${user?.firstName} to you?`}</Typography>
        <Box display='flex' alignItems='center' mt={2} mb={4}>
          <UserAvatar
            url={user?.profilePicThumbURL}
            name={`${user?.firstName} ${user?.lastName}`}
            size={46}
            borderRadius={100}
          />
          <Typography variant='label' flex={1} sx={{ paddingLeft: 1 }}>
            {`${user?.firstName} ${user?.lastName}`}
          </Typography>
        </Box>
        {systemGroups.map((group) => (
          <Box
            display='flex'
            alignItems='center'
            key={group.circleID}
            py={2}
            borderBottom={`1px solid ${colors.veryLightGrey}`}
          >
            <UserAvatar url={group.badgeThumbURL} name={group.circleName} size={46} />

            <Box pl={1} flex={1}>
              <Typography variant='label'>{group.circleName}</Typography>
              <Typography variant='ps'>{`${pluralize('', group.connCount, 'Member', 'Members')}`}</Typography>
            </Box>
            <Radio
              checked={group.circleID === selectedCircleID}
              onChange={handleCircleSelection(group.circleID)}
            />
          </Box>
        ))}

        <Box display='flex' justifyContent={'flex-end'}>
          <RoundedActionButton
            variant={!selectedCircleID ? 'outlined' : 'contained'}
            onClick={onClose}
            sx={{ marginTop: 2 }}
            disabled={!selectedCircleID}
          >
            {true ? 'Accept' : 'Send request'}
          </RoundedActionButton>
        </Box>
      </Box>
    </Drawer>
  )
}

export default CirclesSelectDrawer
